// src/hooks/useCurrentUser/index.ts
import { useCallback } from 'react';

import { useQuery, QueryClient } from '@tanstack/react-query';

import { UseCurrentUserOptions, UseCurrentUserReturn } from './types';
import { queryKeys } from 'lib/query';
import { apiOrchestrator } from 'services/apiService';
import { UserData } from 'types/userData';

const USER_PROFILE_CONFIG = {
  staleTime: 30 * 60 * 1000, // 30 minutes - auth data is stable
  gcTime: 60 * 60 * 1000, // 1 hour
  refetchOnWindowFocus: false,
  refetchOnMount: false,
  retry: 1,
} as const;

const fetchCurrentUser = async (): Promise<UserData> => {
  const response = await apiOrchestrator.get<UserData>('/auth/me');
  return response.data;
};

/**
 * Hook to get current user's profile data via React Query
 */
export function useCurrentUser(options: UseCurrentUserOptions = {}): UseCurrentUserReturn {
  const hasToken = typeof window !== 'undefined' && !!localStorage.getItem('AUTH_TOKEN');
  const { enabled = hasToken } = options;

  const {
    data: user,
    isLoading,
    isFetching,
    error,
    refetch: queryRefetch,
  } = useQuery<UserData, Error>({
    queryKey: queryKeys.users.profile(),
    queryFn: fetchCurrentUser,
    enabled,
    ...USER_PROFILE_CONFIG,
  });

  const refetch = useCallback(() => {
    queryRefetch();
  }, [queryRefetch]);

  return {
    user: user ?? null,
    isLoading: enabled ? isLoading : false,
    isFetching,
    error: error ?? null,
    refetch,
  };
}

/**
 * Pre-populate user cache after login
 */
export function setUserQueryData(queryClient: QueryClient, userData: UserData): void {
  queryClient.setQueryData<UserData>(queryKeys.users.profile(), userData);
}

/**
 * Clear user data from cache on logout
 */
export function clearUserQueryData(queryClient: QueryClient): void {
  queryClient.removeQueries({ queryKey: queryKeys.users.profile() });
  queryClient.removeQueries({ queryKey: queryKeys.users.all });
  queryClient.removeQueries({ queryKey: queryKeys.images.all });
}

export * from './types';
export * from './selectors';
